import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * LOWER BAILEY — the open-air MARKET / TAVERN SQUARE: the festive forecourt just inside the gate, the
 * first beat of the castle. The composition reads in one glance: a HERO ROW of four striped market stalls
 * (crimson · saffron · teal · plum) lines the back of the square, a communal WELL anchors the centre between
 * them, and a garland of heraldic BUNTING is strung high over the whole forecourt. A bench line in front of
 * the stalls invites a sit and a mug; three lampposts pool warm light over the crowd; a couple of ale
 * barrels give market texture. The square stays lively but LEGIBLE — the engine-drawn festival props
 * (mulled-wine cauldron, games table, bard's stage) own the centre lanes, so set-dressing keeps clear of
 * them and leaves the central cross + door mouths open to walk.
 *
 * Geometry: zone (800,2040) 2000×260 → centre (1800,2170). OPEN-AIR — many doors on the N + S edges and the
 * W/E ends, so dressing hugs the back stall line (y≈2110) + the bench line (y≈2170); the door mouths and the
 * central cross stay walkable. Engine props (mulled-cauldron 2000,2120 · games-table 1150,2200 · bard-stage
 * 2450,2200) keep a clear ~40px halo — no decor is piled on them.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── HERO: the row of striped MARKET STALLS along the back of the square, each a different cloth. The
  //    cauldron (2000) + bard-stage (2450) own the right-back, so the stalls frame the left + far ends. ──
  place(ctx, g.marketStall(art, 0xc0492f), 1000, 2110); // crimson
  place(ctx, g.marketStall(art, 0xe0a83a), 1400, 2110); // saffron
  place(ctx, g.marketStall(art, 0x2f7d8a), 2250, 2110); // teal
  place(ctx, g.marketStall(art, 0x7a3f7a), 2650, 2110); // plum

  // ── The communal WELL — the social anchor dead-centre of the back line (clear of the cauldron halo). ──
  place(ctx, g.well(art), 1750, 2120);

  // ── SEATS (load-bearing): a garden bench drawn AT each sit anchor so a duck visibly sits to watch the
  //    bard + share a mug. Coords match bailey-bench-1..6 in seats.ts exactly. ──
  for (const bx of [1100, 1300, 1500, 2100, 2300, 2500]) {
    place(ctx, g.gardenBench(art, 60), bx, 2170);
  }

  // ── POOL LIGHT: three lampposts pool warm light over the crowd — west gate, east end, lit centre. ──
  place(ctx, g.lamppost(art), 850, 2170); // west gateway light
  place(ctx, g.lamppost(art), 2750, 2170); // east end light
  place(ctx, g.lamppost(art), 1800, 2080); // centre, lighting the well + cauldron gather

  // ── BUNTING: heraldic banners strung HIGH across the forecourt (above the heads, a festival garland). ──
  place(ctx, kit.banner(art, 0xc0492f, 70), 1200, 2055);
  place(ctx, kit.banner(art, 0x2f7d8a, 70), 1600, 2050);
  place(ctx, kit.banner(art, 0xe0a83a, 70), 2000, 2055);
  place(ctx, kit.banner(art, 0x2f4a8a, 70), 2400, 2050);

  // ── MARKET TEXTURE: just two ale-barrel accents at the square's edges (off the lanes + door mouths). ──
  place(ctx, kit.barrel(art), 900, 2240);
  place(ctx, kit.barrelStack(art), 2820, 2120);
};

export default decorate;
